import {nextTick, ref} from 'vue';
import {defineStore} from 'pinia';
import {User, UserEditRequest} from '~/types/interfaces/entitites/UserInterface';
import {
    ForgotPasswordRequest,
    LoginRequest,
    RecoverPasswordRequest,
    RegisterRequest
} from '~/types/interfaces/auth/AuthInterface';
import {ErrorInterface} from "~/types/interfaces/ErrorInterface";
import {useCSRF} from "~/stores/csrf";
import {useAPI} from "~/composables/useAPI";
import {Plan} from "~/types/interfaces/entitites/PlanInterface";
import {ModifySubscriptionRequest} from "~/types/interfaces/entitites/SubscriptionInterface";
import {SuccessInterface} from "~/types/interfaces/SuccessInterface";
import {useToken} from "~/stores/token";

export const useAuthStore = defineStore('auth', () => {
    const returnUrl = ref<string | null>(null);
    const error = ref<ErrorInterface | null>(null);
    const succes = ref<SuccessInterface | null>(null);
    const user = ref<User>({} as User);
    const isAuthenticated = ref(false);
    const csrfStore = useCSRF();
    const useApi = useAPI();
    const tokenStore = useToken();

    // computed()s become getters
    const currentUser = computed(() => user.value)
    const currentError = computed(() => error.value)
    const currentReturnUrl = computed(() => returnUrl.value)

    function setAuth(authUser: User) {
        isAuthenticated.value = true;
        user.value = authUser;
        error.value = null;
    }

    function setReturnUrl(url: string) {
        returnUrl.value = url;
    }

    function setError(errorData: any) {
        error.value = errorData;
    }

    function setSuccess(successData: any) {
        succes.value = successData;
    }

    function resetErrors() {
        error.value = null;
    }

    function purgeAuth() {
        isAuthenticated.value = false;
        user.value = {} as User;
        error.value = null;
    }

    async function login(credentials: LoginRequest) {
        resetErrors();

        // Si queremos funcionar con la session de Laravel.
        // if (csrfStore.token === null) {
        //     await csrfStore.csrf();
        // }

        await useApi.apiFetch('/auth/login', {
            method: 'POST',
            body: JSON.stringify(credentials),
        }).then(({data}) => {
            // Si queremos funcionar con la session de Laravel.
            // El login nos devolvería el usuario.
            if (csrfStore.token && data) {
                setAuth(data);
            }
            tokenStore.clearToken();
            tokenStore.setToken(data.token);
            setAuth({} as User);
        }).catch(({response}) => {
            if (response) {
                setError(response._data);
            } else {
                setError({
                        application_code: '',
                        data: {},
                        message: 'No hay conexión',
                        status: 'Error',
                    }
                );
            }
        });
    }

    async function refreshUser() {
        await getUser(true);
    }

    async function getUser(refresh = false) {
        if (!refresh) {
            if (isAuthenticated.value && user.value) {
                return user.value
            }
        }

        const response = await useApi.apiFetch('/client/profile/me')
            .catch(({response}) => {
                purgeAuth();
                if (response) {
                    setError(response._data);
                }
            });
        if (response) {
            setAuth(response.data);
        }

        return user.value
    }

    async function update(data: UserEditRequest) {
        await useApi.apiFetch(`/client/profile/update`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }).then(({data}) => {
            try {
                setAuth(data);
            } catch (e) {
                console.log("exc", e);
            }
        }).catch(({response}) => {
            if (response) {
                setError(response._data);
            } else {
                setError({
                        application_code: '',
                        data: {},
                        message: 'No hay conexión',
                        status: 'Error',
                    }
                );
            }
        });
    }

    async function logout() {
        resetErrors();
        if (isAuthenticated.value && user.value) {
            await useApi.apiFetch('/auth/logout', {
                method: 'POST',
            }).then(({data}) => {
                if (csrfStore.token) {
                    csrfStore.clearToken()
                }
                if (tokenStore.currentToken) {
                    tokenStore.clearToken()
                }
                purgeAuth();
            }).catch(({response}) => {
                if (response) {
                    setError(response._data);
                } else {
                    setError({
                            application_code: '',
                            data: {},
                            message: 'No hay conexión',
                            status: 'Error',
                        }
                    );
                }
            });
        } else {
            if (csrfStore.token) {
                csrfStore.clearToken()
            }
            if (tokenStore.currentToken) {
                tokenStore.clearToken()
            }
            purgeAuth();
        }
    }

    /**
     * Esta función es sólo para la parte cliente. La ponemos aquí para tenerlo desarrollado.
     * @param credentials
     */
    async function register(credentials: RegisterRequest) {
        // Para funcionar con un Token como tal.
        // if (csrfStore.token === null) {
        //     await csrfStore.csrf();
        // }

        await useApi.apiFetch('/client/register/initial-register', {
            method: 'POST',
            body: JSON.stringify(credentials),
        }).then(async ({data}) => {
            // Si queremos funcionar con la session de Laravel.
            // El login nos devolvería el usuario.
            if (csrfStore.token && data) {
                setAuth(data);
            }
            tokenStore.clearToken();
            tokenStore.setToken(data.token);
            // La cookie se escribe de forma diferida; sin esperar aquí, la primera llamada
            // autenticada tras navegar (el middleware global) saldría sin el token todavía.
            await nextTick();
        }).catch(({response}) => {
            if (response) {
                setError(response._data);
            } else {
                setError({
                        application_code: '',
                        data: {},
                        message: 'No hay conexión',
                        status: 'Error',
                    }
                );
            }
        });
    }

    async function verifyRegisterOtp(code: string) {
        await useApi.apiFetch('/client/register/verify-otp', {
            method: 'POST',
            body: JSON.stringify({code}),
        }).then(({data}) => {
            setAuth(data);
        }).catch(({response}) => {
            if (response) {
                setError(response._data);
            } else {
                setError({
                        application_code: '',
                        data: {},
                        message: 'No hay conexión',
                        status: 'Error',
                    }
                );
            }
        });
    }

    async function resendRegisterOtp() {
        await useApi.apiFetch('/client/register/resend-otp', {
            method: 'POST',
        }).then(({data}) => {

        }).catch(({response}) => {
            if (response) {
                setError(response._data);
            } else {
                setError({
                        application_code: '',
                        data: {},
                        message: 'No hay conexión',
                        status: 'Error',
                    }
                );
            }
        });
    }

    /**
     * Corrige el correo introducido en el registro (antes de verificarlo) y reenvía el código OTP.
     * @param email
     */
    async function changeRegisterEmail(email: string) {
        await useApi.apiFetch('/client/register/change-email', {
            method: 'POST',
            body: JSON.stringify({email}),
        }).then(({data}) => {
            setAuth(data);
        }).catch(({response}) => {
            if (response) {
                setError(response._data);
            } else {
                setError({
                        application_code: '',
                        data: {},
                        message: 'No hay conexión',
                        status: 'Error',
                    }
                );
            }
        });
    }

    async function finishRegister(plan: Plan, couponCode: string|undefined = undefined) {
        // Para funcionar con un Token como tal.
        // if (csrfStore.token === null) {
        //     await csrfStore.csrf();
        // }

        await useApi.apiFetch('/client/register/finish-register', {
            method: 'POST',
            body: JSON.stringify({
                'plan_hash': plan.hash,
                'coupon_code': couponCode
            }),
        }).then(({data}) => {

        }).catch(({response}) => {
            if (response) {
                setError(response._data);
            } else {
                setError({
                        application_code: '',
                        data: {},
                        message: 'No hay conexión',
                        status: 'Error',
                    }
                );
            }
        });
    }

    /**
     * Marca como usado el enlace mágico de actualización de plan, una vez el socio ha
     * terminado de verdad el proceso. Si falla, no se muestra error: no es crítico para el
     * cambio de plan en sí, solo evita que el enlace se pueda reutilizar después.
     */
    /**
     * Autoservicio de actualización de plan: pide un código de verificación por correo.
     * Responde siempre igual, exista o no la cuenta, así que no hay forma de saber si falló
     * a partir de la respuesta.
     */
    async function requestPlanUpgradeOtp(email: string) {
        resetErrors();
        await useApi.apiFetch('/client/plan-upgrade/request-otp', {
            method: 'POST',
            body: JSON.stringify({email}),
        }).catch(({response}) => {
            if (response) {
                setError(response._data);
            } else {
                setError({
                        application_code: '',
                        data: {},
                        message: 'No hay conexión',
                        status: 'Error',
                    }
                );
            }
        });
    }

    async function resendPlanUpgradeOtp(email: string) {
        resetErrors();
        await useApi.apiFetch('/client/plan-upgrade/resend-otp', {
            method: 'POST',
            body: JSON.stringify({email}),
        }).catch(({response}) => {
            if (response) {
                setError(response._data);
            } else {
                setError({
                        application_code: '',
                        data: {},
                        message: 'No hay conexión',
                        status: 'Error',
                    }
                );
            }
        });
    }

    /**
     * Verifica el código y devuelve el token de la sesión temporal para actualizar el plan
     * (null si el código no es válido).
     */
    async function verifyPlanUpgradeOtp(email: string, code: string): Promise<string | null> {
        resetErrors();
        let token: string | null = null;

        await useApi.apiFetch('/client/plan-upgrade/verify-otp', {
            method: 'POST',
            body: JSON.stringify({email, code}),
        }).then(({data}) => {
            token = data.token;
        }).catch(({response}) => {
            if (response) {
                setError(response._data);
            } else {
                setError({
                        application_code: '',
                        data: {},
                        message: 'No hay conexión',
                        status: 'Error',
                    }
                );
            }
        });

        return token;
    }

    async function completePlanUpgradeLink(uuid: string) {
        await useApi.apiFetch(`/client/profile/plan-upgrade-link/${uuid}/complete`, {
            method: 'POST',
        }).catch(() => {
        });
    }

    async function modifySubscription(data: ModifySubscriptionRequest) {
        await useApi.apiFetch(`/client/profile/modify-subscription`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }).then(({data}) => {
            try {
                setAuth(data);
                setSuccess(data);
            } catch (e) {
                console.log("exc", e);
            }
        }).catch(({response}) => {
            if (response) {
                setError(response._data);
            } else {
                setError({
                        application_code: '',
                        data: {},
                        message: 'No hay conexión',
                        status: 'Error',
                    }
                );
            }
        });
    }

    async function forgotPassword(credentials: ForgotPasswordRequest) {
        // Para funcionar con un Token como tal.
        // if (csrfStore.token === null) {
        //     await csrfStore.csrf();
        // }

        await useApi.apiFetch('/auth/forgot-password', {
            method: 'POST',
            body: JSON.stringify(credentials),
        }).then(({data}) => {
            // Para funcionar con un Token como tal.
            if (csrfStore.token && data) {
                csrfStore.setToken(data.token)
            }
            setAuth(data);
        }).catch(({response}) => {
            if (response) {
                setError(response._data);
            } else {
                setError({
                        application_code: '',
                        data: {},
                        message: 'No hay conexión',
                        status: 'Error',
                    }
                );
            }
        });
    }

    async function recoverPassword(credentials: RecoverPasswordRequest) {
        // Para funcionar con un Token como tal.
        // if (csrfStore.token === null) {
        //     await csrfStore.csrf();
        // }

        await useApi.apiFetch('/auth/recover-password', {
            method: 'POST',
            body: JSON.stringify(credentials),
        }).then(({data}) => {
            // Para funcionar con un Token como tal.
            if (csrfStore.token && data) {
                csrfStore.setToken(data.token)
            }
            setAuth(data);
        }).catch(({response}) => {
            if (response) {
                setError(response._data);
            } else {
                setError({
                        application_code: '',
                        data: {},
                        message: 'No hay conexión',
                        status: 'Error',
                    }
                );
            }
        });
    }

    return {
        error,
        user,
        isAuthenticated,
        currentUser,
        currentError,
        currentReturnUrl,
        resetErrors,
        login,
        update,
        logout,
        refreshUser,
        getUser,
        register,
        verifyRegisterOtp,
        resendRegisterOtp,
        changeRegisterEmail,
        finishRegister,
        modifySubscription,
        requestPlanUpgradeOtp,
        resendPlanUpgradeOtp,
        verifyPlanUpgradeOtp,
        completePlanUpgradeLink,
        forgotPassword,
        recoverPassword,
        setReturnUrl
    };
});
